import { z } from 'zod'
import { daysUntilExpiry } from './aircraftDocuments'

// The navigation database of each GPS or other unit in an aircraft (issue
// #270, flight.aircraft_nav_data). The club's units follow the 28-day AIRAC
// cycle. Pure, so the admin form can fill the dates in as the pilot types and
// the API judges a unit with exactly the same code.

// A unit this close to running out is flagged before it does.
export const NAV_DATA_WARN_DAYS = 3
// "Days before valid_until" the reminder email goes out. Not the document
// reminder offsets: a cycle lasts 28 days, so a month's notice would fire the
// day the data was loaded.
export const NAV_DATA_REMINDER_DAYS = [7, 1] as const

// AIRAC effective dates are a fixed 28 days apart; 2025-01-23 is cycle 2501.
const AIRAC_EPOCH = '2025-01-23'
const CYCLE_DAYS = 28

const dayNumber = (date: string) => Math.floor(Date.parse(`${date}T00:00:00Z`) / 86_400_000)
const fromDayNumber = (n: number) => new Date(n * 86_400_000).toISOString().slice(0, 10)

export interface AiracCycle {
  // YYNN, e.g. 2611: the year of the effective date and the cycle's number
  // within that year.
  id: string
  effective: string
  // The last day the data may be used: the day before the next cycle.
  validUntil: string
}

// The cycle in force on `date`.
export function airacCycleOn(date: string): AiracCycle {
  const since = dayNumber(date) - dayNumber(AIRAC_EPOCH)
  const effective = fromDayNumber(dayNumber(AIRAC_EPOCH) + Math.floor(since / CYCLE_DAYS) * CYCLE_DAYS)
  const year = Number(effective.slice(0, 4))
  // The first effective date of that year: the earliest one on or after 1 Jan.
  const jan1 = dayNumber(`${year}-01-01`)
  const first = dayNumber(effective) - Math.floor((dayNumber(effective) - jan1) / CYCLE_DAYS) * CYCLE_DAYS
  const number = (dayNumber(effective) - first) / CYCLE_DAYS + 1
  return {
    id: `${String(year).slice(2)}${String(number).padStart(2, '0')}`,
    effective,
    validUntil: fromDayNumber(dayNumber(effective) + CYCLE_DAYS - 1),
  }
}

export type NavDataState = 'current' | 'expiring' | 'expired'

// Valid through `validUntil` inclusive; out of date from the next day.
export function navDataState(validUntil: string, today: string): NavDataState {
  const days = daysUntilExpiry(validUntil, today)
  if (days < 0) return 'expired'
  return days <= NAV_DATA_WARN_DAYS ? 'expiring' : 'current'
}

export const NavDataInputSchema = z
  .object({
    unitName: z.string().trim().min(1).max(60),
    databaseCycle: z.string().trim().max(30).nullable().optional(),
    updatedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    validUntil: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  })
  .refine((v) => v.validUntil >= v.updatedOn, { path: ['validUntil'], message: 'Valid until is before the update date' })
export type NavDataInput = z.infer<typeof NavDataInputSchema>

// GET /api/aircraft/nav-data, GET /api/aircraft/:registration/nav-data
export interface NavDataUnit {
  navDataId: string
  registration: string
  unitName: string
  databaseCycle: string | null
  updatedOn: string
  validUntil: string
  state: NavDataState
}

// The worst of an aircraft's units, for a single chip.
export function worstNavDataState(units: readonly { state: NavDataState }[]): NavDataState | null {
  if (units.length === 0) return null
  if (units.some((u) => u.state === 'expired')) return 'expired'
  return units.some((u) => u.state === 'expiring') ? 'expiring' : 'current'
}
